/**
 * The ship talking about itself.
 *
 * Not the radio. The radio is *other people* — Control talking you onto a pad,
 * a station answering a hail, another pilot on local. This is the hull's own
 * account of what it just did, and it is deliberately a different voice: flat,
 * clipped, faintly officious, and never addressed to anybody.
 *
 * ── Why it is a channel rather than state ───────────────────────────────────
 *
 * Almost everything worth logging happens inside the flight loop, which is a
 * `requestAnimationFrame` chain outside React entirely — it cannot call
 * `setState` sixty times a second, and it should not know what a component is.
 * So the loop *writes* here and the console *subscribes*, the same arrangement
 * `routeChannel` uses and for the same reason.
 *
 * ── Channels, because the loop reports conditions and not events ────────────
 *
 * The loop runs every frame and most of what it notices is still true on the
 * next one. "Mass lock: Groeon" is a fact that holds for a minute and an event
 * that happens once, and a log written straight from a loop is a screen full of
 * one line.
 *
 * So every writer names a **channel** — mass lock, cruise, the hold — and the
 * *state* of that channel. A line is written only when a channel's state changes
 * from what it last was.
 *
 * Deduplicating against the single previous entry instead would look identical
 * in a test with one writer and fail immediately with two: mass lock and cruise
 * both reporting every frame would alternate, each seeing the other as the last
 * key, and both would write forever. Per channel is the difference between a log
 * and a fire hose.
 */

/** How many lines are kept. The console shows fewer; this is the scrollback. */
const LIMIT = 40;

export interface LogEntry {
  /** Stable across renders so a list can key on it without re-typing. */
  id: number;
  /** What the line says. Written as the ship, never as a person. */
  text: string;
  /** For dropping repeats of a condition that is still true. See above. */
  key: string;
  at: number;
  /**
   * The lesson this line is the short version of, when it is one of those.
   *
   * A lesson is said twice — loudly once, then as a sentence here every time —
   * and this is what lets the sentence lead back to the paragraph. Clicking such
   * a line reopens the full card, which is how a player re-reads something they
   * skimmed, and the only way to read one at all if the loud half is switched
   * off. Undefined on the ordinary lines: most of what the ship says is a fact
   * about right now rather than an explanation of a system.
   */
  lesson?: string;
}

export type LogListener = (entries: readonly LogEntry[]) => void;

let entries: LogEntry[] = [];
/** The last state seen on each channel. See the note above. */
const lastState = new Map<string, string>();
let nextId = 1;

const listeners = new Set<LogListener>();

/**
 * Report the state of a channel, and log it if it has changed.
 *
 * Safe to call every frame — that is the whole point. `state` is what decides
 * whether anything happened, so it must describe the *condition* and not carry a
 * changing number: a distance in the state is a line every frame.
 *
 * An empty `text` records the state without saying anything, which is how a
 * writer establishes where it starts without announcing it. The scene reporting
 * "not under cruise" the instant it builds is true and not worth a line.
 */
export function note(
  channel: string,
  state: string,
  text: string,
  options: { lesson?: string; now?: number } = {},
): void {
  if (lastState.get(channel) === state) return;
  lastState.set(channel, state);

  if (!text) return;

  const entry: LogEntry = {
    id: nextId++,
    text,
    key: channel,
    at: options.now ?? Date.now(),
  };
  if (options.lesson) entry.lesson = options.lesson;

  entries = [entry, ...entries].slice(0, LIMIT);
  for (const listener of listeners) listener(entries);
}

/** Fires immediately with the log as it stands, then on every addition. */
export function subscribeLog(listener: LogListener): () => void {
  listeners.add(listener);
  listener(entries);
  return () => listeners.delete(listener);
}

/**
 * Empty it.
 *
 * A crossing tears down the flight scene and builds another; carrying the old
 * system's log into the new one would have the ship reporting a docking that
 * happened somewhere it no longer is.
 */
export function clearLog(): void {
  entries = [];
  lastState.clear();
  for (const listener of listeners) listener(entries);
}
